import { Base, isDirectChild, getDirectChildren } from "@studiometa/js-toolkit";
import { nextFrame, historyPush } from "@studiometa/js-toolkit/utils";
import { FrameAnchor } from "./FrameAnchor.js";
import { FrameForm } from "./FrameForm.js";
import { FrameTarget } from "./FrameTarget.js";
function getScrollPosition() {
  return {
    left: window.pageXOffset,
    top: window.pageYOffset
  };
}
const cache = /* @__PURE__ */ new Map();
class Frame extends Base {
  /**
   * Config.
   */
  static config = {
    name: "Frame",
    emits: [
      "before-fetch",
      "after-fetch",
      "before-leave",
      "after-leave",
      "before-content",
      "after-content",
      "before-enter",
      "after-enter"
    ],
    components: {
      FrameAnchor,
      FrameForm,
      FrameTarget,
      Frame
    },
    options: {
      history: Boolean
    }
  };
  /**
   * Get uniq id.
   */
  get id() {
    return this.$el.id;
  }
  /**
   * Get direct `FrameTarget` children.
   */
  get directChildrenFrameTarget() {
    return getDirectChildren(this, "Frame", "FrameTarget");
  }
  /**
   * Prevent scroll top on unload.
   */
  onWindowUnload() {
    const { history } = window;
    if (!history.state) {
      return;
    }
    history.replaceState(
      {
        ...history.state,
        scroll: getScrollPosition()
      },
      ""
    );
  }
  /**
   * Go to the previous URL on `popstate` event.
   */
  onWindowPopstate({ event }) {
    this.goTo(window.location.href, null, event.state);
  }
  /**
   * Prevent click on `FrameAnchor`.
   */
  onFrameAnchorClick({ target, event }) {
    if (!isDirectChild(this, "Frame", "FrameAnchor", target)) {
      return;
    }
    this.$log("onAFrameClick", target, event);
    event.preventDefault();
    if (target.href === window.location.href) {
      return;
    }
    this.goTo(target.href);
  }
  /**
   * Prevent submit on forms.
   */
  onFrameFormSubmit({ event, target }) {
    if (!isDirectChild(this, "Frame", "FrameForm", target)) {
      return;
    }
    this.$log("onFrameFormFrameSubmit", target, event);
    event.preventDefault();
    const url = new URL(target.action);
    if (target.$el.method === "get") {
      url.search = new URLSearchParams(new FormData(target.$el)).toString();
      this.goTo(url.toString());
    }
    if (target.$el.method === "post") {
      this.goTo(url.toString(), new FormData(target.$el));
    }
  }
  /**
   * Parge an HTML string into a DOM object.
   */
  parseHTML(string = "") {
    return new DOMParser().parseFromString(string, "text/html");
  }
  /**
   * Go to the given url.
   */
  async goTo(url, formData = null, scroll = null) {
    this.$log("goTo", url);
    const parsedUrl = new URL(url);
    if (parsedUrl.origin !== window.location.origin) {
      throw new Error("Cross origin request are not allowed.");
    }
    this.$emit("before-fetch", url);
    const content = await this.fetch(url, formData);
    const doc = this.parseHTML(content);
    const el = doc.querySelector(`#${this.id}`);
    const newFrame = new Frame(el);
    newFrame.$children.registerAll();
    this.$emit("after-fetch", url, content);
    this.$emit("before-leave");
    await Promise.all(this.directChildrenFrameTarget.map((target) => target.leave()));
    this.$emit("after-leave");
    this.$emit("before-content");
    this.directChildrenFrameTarget.map(
      (target, index) => target.updateContent(newFrame.directChildrenFrameTarget[index])
    );
    if (this.$options.history && formData === null) {
      document.title = doc.title;
      historyPush({ path: parsedUrl.pathname, search: parsedUrl.searchParams });
    }
    if (scroll) {
      document.scrollingElement.scrollTop = scroll.top;
      document.scrollingElement.scrollLeft = scroll.left;
    }
    await nextFrame();
    this.$root.$update();
    await nextFrame();
    this.$emit("after-content");
    this.$emit("before-enter");
    await Promise.all(this.directChildrenFrameTarget.map((target) => target.enter()));
    this.$emit("after-enter");
  }
  /**
   * Fetch the given url.
   */
  async fetch(url, formData = null) {
    if (formData) {
      const promise2 = fetch(url, {
        method: "post",
        body: formData
      }).then((response) => response.text());
      const content = await promise2;
      return content;
    }
    const cached = cache.get(url);
    if (cached) {
      if (cached.status === "pending") {
        return cached.promise;
      }
      return cached.content;
    }
    const promise = fetch(url).then((response) => response.text());
    try {
      cache.set(url, {
        promise,
        status: "pending",
        content: void 0
      });
      const content = await promise;
      cache.set(url, {
        promise,
        status: "resolved",
        content
      });
      return content;
    } catch (err) {
      cache.set(url, {
        promise,
        status: "error",
        content: err
      });
      return err;
    }
  }
}
export {
  Frame
};
//# sourceMappingURL=Frame.js.map
